import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Users, DollarSign, Calendar, Loader2, Archive, Mail, MailCheck, AlertTriangle, X } from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminDonorStatementsTab = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [emailing, setEmailing] = useState(null); // donor email or 'bulk'
  const [lastSentMap, setLastSentMap] = useState({});
  const [history, setHistory] = useState([]);
  const [cooldownHours, setCooldownHours] = useState(24);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [forceResend, setForceResend] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [toast, setToast] = useState(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, donorsRes, histRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/donor-statements/summary?year=${year}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/donor-statements/donors?year=${year}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/donor-statements/email-history?year=${year}`, { withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      setDonors(donorsRes.data);
      setLastSentMap(histRes.data?.last_sent_map || {});
      setHistory(histRes.data?.history || []);
      setCooldownHours(histRes.data?.cooldown_hours || 24);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  };

  const downloadStatement = async (email) => {
    setDownloading(email);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/donor-statements/download/${encodeURIComponent(email)}?year=${year}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `NHF_Statement_${year}_${email.replace('@', '_at_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e.response?.status === 404 ? `No completed donations found for ${email} in ${year}` : 'Download failed', 'error');
    }
    setDownloading(null);
  };

  const downloadAll = async () => {
    setDownloading('bulk');
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/donor-statements/bulk?year=${year}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `NHF_Statements_${year}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e.response?.status === 404 ? `No completed donations found for ${year}` : 'Bulk download failed', 'error');
    }
    setDownloading(null);
  };

  const emailStatement = async (donorEmail, force = false) => {
    setEmailing(donorEmail);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/donor-statements/send/${encodeURIComponent(donorEmail)}?year=${year}&force=${force}`,
        {},
        { withCredentials: true }
      );
      const status = res.data?.status;
      if (status === 'sent') {
        showToast(`Statement emailed to ${donorEmail}`, 'success');
      } else if (status === 'skipped' && res.data?.reason === 'cooldown') {
        if (window.confirm(`A statement was already sent to ${donorEmail} within the past ${cooldownHours} hours. Resend anyway?`)) {
          setEmailing(null);
          return emailStatement(donorEmail, true);
        }
        showToast(`Skipped — already sent within ${cooldownHours}h`, 'info');
      } else {
        showToast(`Skipped — ${res.data?.reason || 'unknown reason'}`, 'info');
      }
      await fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send email';
      showToast(`Send failed: ${detail}`, 'error');
    }
    setEmailing(null);
  };

  const bulkEmail = async () => {
    setEmailing('bulk');
    setShowBulkConfirm(false);
    setBulkResult(null);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/donor-statements/bulk-send`,
        { year, force: forceResend },
        { withCredentials: true }
      );
      setBulkResult(res.data);
      const c = res.data?.counts || {};
      showToast(`Bulk send complete — Sent: ${c.sent || 0}, Skipped: ${c.skipped || 0}, Failed: ${c.failed || 0}`, 'success');
      await fetchData();
    } catch (e) {
      showToast(`Bulk send failed: ${e.response?.data?.detail || e.message}`, 'error');
    }
    setEmailing(null);
    setForceResend(false);
  };

  return (
    <div className="space-y-6" data-testid="donor-statements-tab">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-md shadow-lg text-sm font-medium max-w-md ${
            toast.type === 'error' ? 'bg-red-600 text-white' :
            toast.type === 'info' ? 'bg-amber-500 text-white' :
            'bg-green-600 text-white'
          }`}
          data-testid="donor-statement-toast"
        >
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Year-End Donor Statements</h2>
          <p className="text-xs text-[#4B5563]">Generate, download, and email annual giving summaries for donors</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none"
            data-testid="statement-year-select">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setShowHistory(true)}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F8F9FA]"
            data-testid="view-history-btn">
            <MailCheck className="w-3.5 h-3.5" /> Email History
          </button>
          {donors.length > 0 && (
            <>
              <button onClick={downloadAll} disabled={downloading === 'bulk'}
                className="px-3 py-2 border border-[#E5E7EB] rounded-md text-xs flex items-center gap-1.5 hover:bg-[#F8F9FA] disabled:opacity-50"
                data-testid="bulk-download-btn">
                {downloading === 'bulk' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                Download All ZIP ({donors.length})
              </button>
              <button onClick={() => setShowBulkConfirm(true)} disabled={emailing === 'bulk'}
                className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
                data-testid="bulk-email-btn">
                {emailing === 'bulk' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                Email All Donors
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="statement-summary">
          <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#4B5563]">Total Raised</span>
              <DollarSign className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-xl font-bold text-green-700" data-testid="total-raised">${summary.total_raised.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </div>
          <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#4B5563]">Unique Donors</span>
              <Users className="w-4 h-4 text-[#BA1826]" />
            </div>
            <p className="text-xl font-bold text-[#0A0A0A]" data-testid="total-donors">{summary.total_donors}</p>
          </div>
          <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#4B5563]">Total Donations</span>
              <FileText className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-xl font-bold text-[#0A0A0A]">{summary.total_donations}</p>
          </div>
          <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#4B5563]">Year</span>
              <Calendar className="w-4 h-4 text-[#4B5563]" />
            </div>
            <p className="text-xl font-bold text-[#0A0A0A]">{summary.year}</p>
          </div>
        </div>
      )}

      {/* Bulk-send result panel */}
      {bulkResult && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="bulk-result-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-[#0A0A0A]">Last Bulk Send Result — {bulkResult.year}</h3>
            <button onClick={() => setBulkResult(null)} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="close-bulk-result">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-[10px] uppercase tracking-wide text-green-700 font-semibold">Sent</p>
              <p className="text-xl font-bold text-green-800">{bulkResult.counts?.sent || 0}</p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Skipped</p>
              <p className="text-xl font-bold text-amber-800">{bulkResult.counts?.skipped || 0}</p>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Failed</p>
              <p className="text-xl font-bold text-red-800">{bulkResult.counts?.failed || 0}</p>
            </div>
          </div>
          {(bulkResult.results || []).filter(r => r.status !== 'sent').length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[#4B5563] font-medium">View skipped / failed details</summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {bulkResult.results.filter(r => r.status !== 'sent').map((r, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-[#F1F5F9] py-1">
                    <span className="text-[#0A0A0A]">{r.email || '(no email)'} — <span className="text-[#9CA3AF]">{r.name}</span></span>
                    <span className={r.status === 'failed' ? 'text-red-700' : 'text-amber-700'}>
                      {r.status}: {r.reason || r.error}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Donor List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : donors.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <FileText className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">No completed donations found for {year}</p>
          <p className="text-xs text-[#9CA3AF] mt-1">Statements are generated from completed Stripe payments only</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="donors-table">
            <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Donor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Total Given</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Gifts</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Period</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4B5563]">Last Emailed</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[#4B5563]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {donors.map((d) => {
                const lastSent = lastSentMap[d.email];
                return (
                  <tr key={d.email} className="border-b border-[#F1F5F9] last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0A0A0A]">{d.name}</p>
                      <p className="text-xs text-[#9CA3AF]">{d.email}</p>
                    </td>
                    <td className="px-4 py-3 font-semibold text-green-700">${d.total_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    <td className="px-4 py-3 text-[#4B5563]">{d.donation_count}</td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{d.first_donation} — {d.last_donation}</td>
                    <td className="px-4 py-3 text-xs">
                      {lastSent ? (
                        <span className="inline-flex items-center gap-1 text-green-700" data-testid={`last-sent-${d.email}`}>
                          <MailCheck className="w-3 h-3" /> {fmtDate(lastSent)}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button onClick={() => downloadStatement(d.email)} disabled={downloading === d.email}
                          className="inline-flex items-center gap-1 text-xs text-[#4B5563] font-medium hover:text-[#0A0A0A] disabled:opacity-50"
                          data-testid={`download-statement-${d.email}`}>
                          {downloading === d.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                          PDF
                        </button>
                        <button onClick={() => emailStatement(d.email)} disabled={emailing === d.email}
                          className="inline-flex items-center gap-1 text-xs text-[#BA1826] font-semibold hover:underline disabled:opacity-50"
                          data-testid={`email-statement-${d.email}`}>
                          {emailing === d.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                          Email
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk Confirm Modal */}
      {showBulkConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-confirm-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#BA1826]" />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Email All Donors?</h3>
                <p className="text-sm text-[#4B5563] mt-1">
                  This will email a {year} year-end giving statement (PDF attached) to <b>{donors.length} donor{donors.length === 1 ? '' : 's'}</b> via Resend.
                </p>
              </div>
            </div>
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-md p-3 mb-4 text-xs text-[#4B5563] space-y-1">
              <p>• Donors emailed within the last {cooldownHours} hours will be <b>skipped</b> unless you force resend.</p>
              <p>• Invalid email addresses will be skipped and reported.</p>
              <p>• Each send is logged to history with timestamp + Resend ID.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#0A0A0A] mb-4 cursor-pointer">
              <input type="checkbox" checked={forceResend} onChange={(e) => setForceResend(e.target.checked)}
                className="rounded border-[#E5E7EB]" data-testid="force-resend-checkbox" />
              <span>Force resend (ignore {cooldownHours}h cooldown)</span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setShowBulkConfirm(false); setForceResend(false); }}
                className="px-4 py-2 text-sm text-[#4B5563] hover:bg-[#F8F9FA] rounded-md"
                data-testid="bulk-cancel-btn">
                Cancel
              </button>
              <button onClick={bulkEmail}
                className="btn-primary text-sm flex items-center gap-1.5"
                data-testid="bulk-confirm-btn">
                <Mail className="w-3.5 h-3.5" /> Send to {donors.length} donor{donors.length === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="history-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Statement Email History — {year}</h3>
              <button onClick={() => setShowHistory(false)} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="close-history">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {history.length === 0 ? (
                <p className="text-sm text-[#4B5563] text-center py-8">No email attempts recorded for {year}.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-[#4B5563]">When</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#4B5563]">Donor</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#4B5563]">Status</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#4B5563]">Triggered By</th>
                      <th className="text-left px-3 py-2 font-semibold text-[#4B5563]">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className="border-b border-[#F1F5F9]">
                        <td className="px-3 py-2 text-[#4B5563]">{fmtDate(h.sent_at)}</td>
                        <td className="px-3 py-2 text-[#0A0A0A]">{h.donor_email}</td>
                        <td className="px-3 py-2">
                          <span className={
                            h.status === 'sent' ? 'inline-block px-2 py-0.5 rounded bg-green-100 text-green-800 font-semibold' :
                            h.status === 'failed' ? 'inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold' :
                            'inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold'
                          }>
                            {h.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-[#4B5563]">{h.triggered_by || '—'}</td>
                        <td className="px-3 py-2 text-[#9CA3AF]">{h.error || h.resend_id || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDonorStatementsTab;
